import { FileUploadDomHelper } from './file-upload-dom.helper';
import { getUppyLocale } from '../../helpers/uppy-locales';
import { FileUploadOptionsModel } from '../../models/interfaces/file-upload.model';

export class FileUploadOptionsProvider {
    private readonly MAX_FILE_SIZE = 10485760; // 10MB
    private readonly UPLOAD_ENDPOINT = '/file-management-api/upload';

    private domHelper: FileUploadDomHelper;

    constructor(domHelper: FileUploadDomHelper) {
        this.domHelper = domHelper;
    }

    getDefaultOptions(
        target?: string,
        trigger?: string | null,
        lang: string = 'en'
    ): FileUploadOptionsModel {
        return {
            general: this.getGeneralOptions(),
            dashboard: this.getDashboardOptions(target, trigger, lang),
            xhr: this.getXhrOptions(),
            public: true,
            category: 'Default',
            overrides: this.getDefaultDomOverrides()
        };
    }

    private getGeneralOptions(): FileUploadOptionsModel['general'] {
        return {
            autoProceed: false,
            restrictions: {
                maxFileSize: this.MAX_FILE_SIZE,
                maxNumberOfFiles: 5,
                allowedFileTypes: ['.jpg', '.jpeg', '.png', '.gif', '.bmp']
            }
        };
    }

    private getDashboardOptions(
        target?: string,
        trigger?: string | null,
        lang: string = 'en'
    ): any {
        const processedLang = lang.substring(0, 2);
        if (!target) {
            target = `#file-management-dashboard-${this.generateRandomId()}`;
            this.domHelper.createTargetElement(target);
        }

        return {
            inline: false,
            note: this.getFileSizeNote(),
            target: target,
            trigger,
            proudlyDisplayPoweredByUppy: false,
            showProgressDetails: true,
            closeModalOnClickOutside: false,
            locale: getUppyLocale(processedLang),
            closeAfterFinish: false
        };
    }

    private getXhrOptions(): FileUploadOptionsModel['xhr'] {
        return {
            endpoint: this.UPLOAD_ENDPOINT,
            method: 'POST',
            allowedMetaFields: ['filename', 'public', 'category']
        };
    }

    private getDefaultDomOverrides(): { selector: string; type: string; override: string; }[] {
        return [
            { selector: '.uppy-StatusBar-actionBtn--upload', type: 'id', override: 'uppy-upload-btn' },
            { selector: '.uppy-Dashboard-close', type: 'id', override: 'uppy-close-btn' },
            { selector: '[data-uppy-acquirer-id="MyDevice"] button', type: 'id', override: 'uppy-my-device-btn' },
            { selector: '[data-uppy-acquirer-id="MyDevice"] button', type: 'aria-label', override: 'My Device' },
            { selector: '[data-uppy-acquirer-id="DragDrop"] button', type: 'id', override: 'uppy-drag-and-drop-btn' },
            { selector: '[data-uppy-acquirer-id="DragDrop"] button', type: 'aria-label', override: 'Drag & Drop' },
            { selector: '.uppy-DashboardContent-back', type: 'id', override: 'uppy-back-btn' },
            { selector: '.uppy-DashboardContent-back', type: 'aria-label', override: 'Cancel Button' },
            { selector: '.uppy-DashboardContent-addMore', type: 'id', override: 'uppy-add-more-btn' },
            { selector: '.uppy-StatusBar-actionBtn--done', type: 'id', override: 'uppy-done-btn' },
            { selector: '.uppy-Dashboard-browse', type: 'id', override: 'uppy-browse-btn' },
            { selector: '.uppy-Dashboard-browse', type: 'aria-label', override: 'Browse Files' },
            { selector: '.uppy-Dashboard-browse', type: 'role', override: 'button' },
            { selector: '.uppy-DragDrop-input', type: 'id', override: 'uppy-drag-and-drop-input' },
            { selector: '.uppy-DragDrop-inner', type: 'role', override: 'button' },
            { selector: '.uppy-DragDrop-inner', type: 'aria-label', override: 'Drop Here or Browse' }
        ];
    }

    private getFileSizeNote(): string {
        const sizeInMB = Math.round(this.MAX_FILE_SIZE / 1024 / 1024);
        return `Maximum allowed size ${sizeInMB} MB.`;
    }

    private generateRandomId(): string {
        return Math.floor(Math.random() * 1000).toString();
    }
}
